import React, { useCallback, useMemo, useState } from "react";
import { useFinancialAccounts } from "@hooks/useFinancialAccounts";
import { DirectorySort } from "@common/constants/counterparty";
import { RegisteredCounterpartyIncludeScope } from "@common/helpers/counterparties";
import { pluralize } from "@common/helpers/string";
import {
    RuleActionType,
    RuleConditionType,
    RuleGroupType,
    RuleSource,
    TransactionRuleActionDefinition,
    TransactionRuleConditionDefinition,
} from "@common/types/transactionRule";
import { useActiveWorkspace } from "@hooks/useActiveWorkspace";
import { useOverlays } from "contexts/OverlayContext";
import { useCategoryLabelGetter } from "@hooks/useCategoryLabelGetter";
import { ButtonWithLoader } from "@design-system/Button/ButtonWithLoader";
import { Button } from "@design-system/Button/Button";
import { useCreateRule } from "@api/rules.api";
import { useCounterpartiesQuery } from "@api/counterparty.api";
import { HStack } from "@design-system/HStack/HStack";
import styles from "./RuleCreationToast.module.scss";

interface RuleCreationToastData {
    conditions: TransactionRuleConditionDefinition[];
    actions: TransactionRuleActionDefinition[];
    transactionsNumber: number;
    canCreateWithtoutReview: boolean;
    applyToExistingTransactions?: boolean;
    displayedChangedEntityName?: string;
}
export const RuleCreationToast: React.FC<RuleCreationToastData> = ({
    conditions,
    transactionsNumber,
    actions,
    canCreateWithtoutReview: canCreateWithoutReview,
    applyToExistingTransactions,
    displayedChangedEntityName,
}) => {
    const { openOverlay } = useOverlays();
    const labelGetter = useCategoryLabelGetter();

    const { data: counterparties } = useCounterpartiesQuery({
        sort: DirectorySort.NAME_ASC,
        includeRegistered: RegisteredCounterpartyIncludeScope.USED,
        includeUsageStats: false,
    });
    const accounts = useFinancialAccounts();
    const activeWorkspace = useActiveWorkspace();
    const [ruleAdded, setRuleAdded] = useState(false);
    const ruleCreation = useCreateRule(() => setRuleAdded(true));

    const conditionLabel = useMemo(() => {
        if (!conditions) {
            return;
        }

        return conditions.map((condition) => {
            let label = "";
            if (condition.conditionType === RuleConditionType.COUNTERPARTY) {
                label += counterparties?.data
                    ?.filter((cp) =>
                        (condition.value as string[]).includes(
                            cp.counterparty.id,
                        ),
                    )
                    ?.map((cp) => cp.counterparty.name)
                    .join(", ");
            }

            if (
                condition.conditionType === RuleConditionType.FINANCIAL_ACCOUNTS
            ) {
                label += accounts
                    ?.filter((account) =>
                        (condition.value as number[]).includes(account.id),
                    )
                    ?.map((account) => account.name)
                    .join(", ");
            }

            return label;
        });
    }, [conditions, counterparties, accounts]);

    const actionLabel = useMemo(() => {
        if (!actions) {
            return;
        }

        return actions.map((action) => {
            if (action.actionType === RuleActionType.CATEGORY) {
                return labelGetter(action.value as string);
            }
            if (action.actionType === RuleActionType.ACCOUNT_ID) {
                return displayedChangedEntityName;
            }
        });
    }, [actions, labelGetter, displayedChangedEntityName]);

    const isAccountAction =
        actions?.at(0)?.actionType === RuleActionType.ACCOUNT_ID;

    const newRuleDefinition = useMemo(
        () => ({
            conditions,
            actions,
            applyToExistingTransactions,
        }),
        [conditions, actions, applyToExistingTransactions],
    );
    const ruleCreationPayload = useMemo(() => {
        if (!activeWorkspace || !newRuleDefinition) {
            return;
        }

        return {
            workspaceId: activeWorkspace.id,
            applyToExistingTransactions: true,
            rule: newRuleDefinition,
            source: RuleSource.TRANSACTIONS_FEED,
        };
    }, [activeWorkspace, newRuleDefinition]);

    const reviewRule = useCallback(() => {
        if (!newRuleDefinition) {
            return;
        }

        openOverlay("createRule", {
            definition: {
                conditions: newRuleDefinition.conditions,
                actions: newRuleDefinition.actions,
                applyToExistingTransactions:
                    newRuleDefinition.applyToExistingTransactions,
            },
            source: RuleSource.TRANSACTIONS_FEED,
            groupType: RuleGroupType.TRANSACTION,
        });
    }, [newRuleDefinition, openOverlay]);

    return (
        <div className={styles.message}>
            <p>
                Always {isAccountAction ? "assign" : "categorize"}{" "}
                <span className={styles.conditionLabel}>{conditionLabel}</span>{" "}
                {isAccountAction ? "to" : "as"}{" "}
                <span className={styles.actionLabel}>{actionLabel}</span>
                {transactionsNumber > 0 ? (
                    <>
                        {" "}
                        and apply to{" "}
                        <strong>
                            {transactionsNumber} similar{" "}
                            {pluralize("transaction", transactionsNumber)}
                        </strong>
                    </>
                ) : null}
                ?
            </p>

            <footer className={styles.footer}>
                {ruleAdded ? (
                    "Rule created"
                ) : (
                    <HStack gap={12}>
                        <Button
                            variant="secondary"
                            size="md"
                            onClick={reviewRule}
                            className={styles.review}
                        >
                            Review
                        </Button>
                        {canCreateWithoutReview ? (
                            <ButtonWithLoader
                                variant="primary"
                                size="md"
                                onClick={() => {
                                    if (!ruleCreationPayload) {
                                        return;
                                    }

                                    ruleCreation.mutate(ruleCreationPayload);
                                }}
                                loading={ruleCreation.isPending}
                            >
                                Create rule
                            </ButtonWithLoader>
                        ) : null}
                    </HStack>
                )}
            </footer>
        </div>
    );
};
